import React from 'react';
import { pluralize } from '@support/string';
import { ClientEnv } from '@app/types';
import { DeviceIntegrationProvider } from '@models/Integration';
import { inlineLabelForDeviceIntegrationType, titleForDeviceIntegrationType } from './utils';

export interface UnsupportedIntegrationConfig {
  integrationType: DeviceIntegrationProvider;
  whereSupported: Array<ClientEnv>;
  docsLink?: string;
  directUser?: boolean;
  forceDarkMode?: boolean;
}

const displayWhereSupported = (whereSupported: Array<string>, connector: 'or' | 'and'): string => {
  let envs = whereSupported[0];

  if (whereSupported.length == 2) {
    envs = whereSupported.join(` ${connector} `);
  } else if (whereSupported.length > 2) {
    const _whereSupported = [...whereSupported];
    _whereSupported[_whereSupported.length - 1] = `${connector} ${
      _whereSupported[_whereSupported.length - 1]
    }`;
    envs = _whereSupported.join(', ');
  }

  return `${envs} ${pluralize(whereSupported.length, 'app')}`;
};

const UnsupportedMessage = ({ label, whereSupported }) => (
  <>
    Connecting your {label} account is only supported in Clay's{' '}
    {displayWhereSupported(whereSupported, 'and')}. Don't worry, you will be able to set this up
    later!
  </>
);

export const UnsupportedMessageWithDirection = ({ label, whereSupported }) => (
  <>
    To connect your {label} to Clay, please use the {displayWhereSupported(whereSupported, 'or')}.{' '}
    <a href="https://clay.earth/start" target="_blank">
      Get started with our other apps.
    </a>
  </>
);

export const promptForUnsupportedIntegration =
  ({
    integrationType,
    whereSupported,
    docsLink,
    directUser,
    forceDarkMode,
  }: UnsupportedIntegrationConfig) =>
  () => {
    const label = inlineLabelForDeviceIntegrationType[integrationType];
    let title = `Connect ${label}`;
    let message = <UnsupportedMessage label={label} whereSupported={whereSupported} />;

    if (directUser) {
      title = `Clay Access to ${label}`;
      message = <UnsupportedMessageWithDirection label={label} whereSupported={whereSupported} />;
    }

    window.app.confirm(
      {
        title,
        message,
        action: directUser && !!docsLink ? 'Learn more' : 'OK',
        forceDarkMode,
      },
      (confirmed: boolean) => {
        if (confirmed && directUser && docsLink) {
          window.open(`https://go.clay.earth/${docsLink}`, '_blank');
        }
      },
    );
  };

export const promptForUnsupportedConnectedIntegration = ({ integrationType, forceDarkMode }) => {
  const title = titleForDeviceIntegrationType[integrationType];

  window.app.confirm(
    {
      title: title,
      message: `Please use Clay for iOS to manage your connected ${title} account`,
      noCancelButton: true,
      action: 'OK',
      forceDarkMode,
    },
    () => {},
  );
};
